import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { IAMService, PersonaType, SortDirection, User, UserSortField } from '@vendasta/iam';
import { AssociatedUserInterface, UserService as BusinessCenterUserService } from '@vendasta/business-center';
import { BadgeColor } from '@vendasta/galaxy/badge';
import {
  Observable,
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  from,
  map,
  of,
  shareReplay,
  startWith,
  switchMap,
  take,
  timer,
} from 'rxjs';
import { InboxService } from '../../../../core/src/lib/inbox.service';
import { ConversationDetail } from '../../../../core/src/lib/interface/conversation.interface';
import { ConversationService } from '../../../../core/src/lib/state/conversation.service';
import { ACCOUNT_GROUP_ID_TOKEN, PARTNER_ID_TOKEN, USER_ID_TOKEN } from '../../../../core/src/lib/tokens';

export type AssigneeState = 'unassigned' | 'assigned-to-me' | 'assigned-to-other';

export interface AssignableUser {
  userId: string;
  displayName: string;
}

@Injectable()
export class ConversationAssignService {
  private readonly inboxService = inject(InboxService);
  private readonly iamService = inject(IAMService);
  private readonly bcUserService = inject(BusinessCenterUserService);
  private readonly conversationService = inject(ConversationService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly partnerId = toSignal(inject(PARTNER_ID_TOKEN));
  private readonly accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  private readonly currentUserId = toSignal(inject(USER_ID_TOKEN));
  private readonly destroyRef = inject(DestroyRef);

  private readonly searchTerm$$ = new Subject<string>();
  private conversationDetail = signal<ConversationDetail | null>(null);

  readonly selectedUser = signal<AssignableUser | null>(null);
  readonly assigning = signal(false);
  readonly resolvingName = signal(false);

  readonly $assigneeState = computed<AssigneeState>(() => {
    const user = this.selectedUser();
    if (!user) return 'unassigned';
    const currentUserId = this.currentUserId();
    if (currentUserId && user.userId === currentUserId) return 'assigned-to-me';
    return 'assigned-to-other';
  });

  readonly $badgeColor = computed<BadgeColor>(() => {
    return this.$assigneeState() === 'assigned-to-me' ? 'blue' : 'grey';
  });

  readonly users$: Observable<AssignableUser[]> = this.searchTerm$$.pipe(
    startWith(''),
    debounceTime(300),
    map((v) => (typeof v === 'string' ? v : '')),
    distinctUntilChanged(),
    switchMap((term) => this.searchUsers(term)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  setSearchTerm(term: string): void {
    this.searchTerm$$.next(term);
  }

  setConversationDetail(detail: ConversationDetail | null): void {
    this.conversationDetail.set(detail);
    this.syncAssigneeFromDetail(detail);
  }

  assignToMe(): void {
    const userId = this.currentUserId();
    if (!userId) return;
    this.users$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
      map((users) => users.find((u) => u.userId === userId)),
    ).subscribe((cached) => {
      this.assignUser(cached ?? { userId, displayName: '' });
    });
  }

  assignUser(user: AssignableUser | null): void {
    const conversationId = this.conversationDetail()?.conversation?.conversationId;
    if (!conversationId) {
      this.snackbarService.openErrorSnack('No active conversation');
      return;
    }

    this.assigning.set(true);

    forkJoin({
      rpc: this.conversationService.assignConversation$(conversationId, user ? [user.userId] : []),
      minDelay: timer(400),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.selectedUser.set(user);
          this.assigning.set(false);
        },
        error: (err) => {
          this.assigning.set(false);
          this.snackbarService.openErrorSnack(err);
        },
      });
  }

  private syncAssigneeFromDetail(detail: ConversationDetail | null): void {
    if (this.assigning()) return;
    const assigneeIds = (detail?.conversation as { assigneeUserIds?: string[] })
      ?.assigneeUserIds;
    const assigneeId = assigneeIds?.[0];
    if (assigneeId === this.selectedUser()?.userId) return;
    if (assigneeId) {
      this.selectedUser.set({ userId: assigneeId, displayName: '' });
      this.resolveAssigneeName(assigneeId);
    } else {
      this.selectedUser.set(null);
    }
  }

  private resolveAssigneeName(userId: string): void {
    this.resolvingName.set(true);
    this.users$.pipe(
      take(1),
      switchMap((users) => {
        const cached = users.find((u) => u.userId === userId);
        // Assignees are often partner/staff who aren't associated users of
        // the account group, so they're absent from the cached list. Fall
        // back to a direct IAM lookup only on a miss to keep the shared
        // fetch as the common path (see WOW-2605).
        return cached ? of(cached) : this.lookupUserById(userId);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((resolved) => {
      const current = this.selectedUser();
      if (current?.userId === userId) {
        this.selectedUser.set(resolved);
      }
      this.resolvingName.set(false);
    });
  }

  private lookupUserById(userId: string): Observable<AssignableUser> {
    return this.iamService.getMultiUsers([{ userId }]).pipe(
      map((users) => {
        const user = users?.[0];
        return user
          ? this.toAssignable(user)
          : { userId, displayName: '' };
      }),
      catchError(() => of({ userId, displayName: '' })),
    );
  }

  private searchUsers(term: string): Observable<AssignableUser[]> {
    return this.inboxService.isBusinessApp
      ? this.searchBusinessUsers(term)
      : this.searchPartnerUsers(term);
  }

  private searchBusinessUsers(term: string): Observable<AssignableUser[]> {
    const accountGroupId = this.accountGroupId();
    if (!accountGroupId) return of([]);

    return from(this.bcUserService.listUsersForBusiness(accountGroupId)).pipe(
      map((resp) => (resp?.associatedUsers ?? []).map((u) => this.toAssignableFromAssociated(u))),
      map((users) => this.filterByTerm(users, term)),
      catchError((err) => {
        console.warn('[assign-picker] user search unavailable; picker will show empty list', { accountGroupId, err });
        return of([] as AssignableUser[]);
      }),
    );
  }

  private searchPartnerUsers(term: string): Observable<AssignableUser[]> {
    const namespace = this.partnerId();
    if (!namespace) return of([]);

    return this.iamService
      .listUsers(
        namespace, undefined, '', 25,
        [PersonaType.partner],
        term ? [term] : [],
        [
          { direction: SortDirection.SORT_DIRECTION_ASCENDING, field: UserSortField.USER_SORT_FIELD_FIRST_NAME },
          { direction: SortDirection.SORT_DIRECTION_ASCENDING, field: UserSortField.USER_SORT_FIELD_LAST_NAME },
        ],
      )
      .pipe(
        map((resp) => (resp.users ?? []).map((u) => this.toAssignable(u))),
        catchError((err) => {
          console.warn('[assign-picker] user search unavailable; picker will show empty list', { namespace, err });
          return of([] as AssignableUser[]);
        }),
      );
  }

  private filterByTerm(users: AssignableUser[], term: string): AssignableUser[] {
    if (!term) return users;
    const needle = term.toLowerCase();
    return users.filter((u) => u.displayName.toLowerCase().includes(needle));
  }

  private buildDisplayName(
    firstName?: string | null,
    lastName?: string | null,
    email?: string | null,
    userId?: string | null,
  ): string {
    const name = [firstName, lastName]
      .filter(Boolean).join(' ').trim();
    return name || email || userId || '';
  }

  private toAssignable(user: User): AssignableUser {
    return {
      userId: user.userId,
      displayName: this.buildDisplayName(
        user.firstName, user.lastName,
        user.email, user.userId,
      ),
    };
  }

  private toAssignableFromAssociated(user: AssociatedUserInterface): AssignableUser {
    return {
      userId: user.userId || '',
      displayName: this.buildDisplayName(
        user.firstName, user.lastName,
        user.email, user.userId,
      ),
    };
  }
}
